/**
 * The operator, as `zapio-backend` reports them.
 *
 * Shape verified against `zapio-backend`'s `AdminActorResponse` in
 * `admin-session.mapper.ts`. Snake_case because that is the wire, and renaming it here
 * would be a translation table to read in two files while debugging one field.
 *
 * `permissions` arrives **already expanded for a super-admin** — the backend substitutes
 * the whole catalogue — so nothing here special-cases that.
 */
export interface Operator {
  readonly id: string
  readonly email: string
  readonly name: string
  readonly is_super_admin: boolean
  readonly roles: readonly string[]
  readonly permissions: readonly string[]
  /**
   * The backend's guard enforces this regardless, answering 403 on every other admin
   * route until it is cleared. This field exists so the panel can send the operator
   * straight to the right screen — it is the UX, not the security.
   */
  readonly must_change_password: boolean
  /**
   * The second factor (FW-23). `permissions` above is already EFFECTIVE — a high-risk
   * permission held without an authenticator is listed here instead, so the panel hides
   * the control and says why in one place.
   */
  readonly two_factor: {
    readonly enabled: boolean
    readonly required: boolean
    readonly suspended_permissions: readonly string[]
  }
}

/** `POST /admin/auth/login` answers with the operator, or with this one flag and a challenge cookie. */
export interface TwoFactorRequired {
  readonly two_factor_required: true
}

export type LoginResponse = Operator | TwoFactorRequired

export function isTwoFactorRequired(response: LoginResponse): response is TwoFactorRequired {
  return 'two_factor_required' in response
}

/**
 * `POST /admin/auth/login` and `GET /admin/auth/me` answer with the operator **directly** —
 * there is no wrapper object.
 *
 * Worth stating because the obvious guess is `{ actor: … }`, and a guessed response shape
 * typechecks, renders nothing, and gives no error to follow. The session itself arrives as
 * `Set-Cookie`; no response here contains a token.
 */
export type SessionResponse = Operator

/** `POST /admin/auth/two-factor` — the code, or a recovery code; the server tells them apart. */
export interface TwoFactorCodeInput {
  readonly code: string
}

export interface Credentials {
  readonly email: string
  readonly password: string
}
